"use client"

import { supabase } from './supabase'

// Global auth error handler
export class AuthInterceptor {
  private static retryCount = 0
  private static maxRetries = 5
  private static isRefreshing = false
  private static refreshPromise: Promise<boolean> | null = null

  // Handle 401/403 errors globally
  static async handleAuthError(error: Response): Promise<boolean> {
    if (error.status !== 401 && error.status !== 403) {
      return false
    }

    // Prevent infinite retry loops
    if (this.retryCount >= this.maxRetries) {
      this.retryCount = 0
      this.redirectToLogin()
      return false
    }

    this.retryCount++
    
    // Add exponential backoff delay between retries
    const delayMs = Math.min(1000 * Math.pow(2, this.retryCount - 1), 5000)
    console.log(`Waiting ${delayMs}ms before retry attempt ${this.retryCount}`)
    await new Promise(resolve => setTimeout(resolve, delayMs))

    // If already refreshing, wait for that to complete
    if (this.isRefreshing && this.refreshPromise) {
      return this.refreshPromise
    }

    // Start refresh process
    this.isRefreshing = true
    this.refreshPromise = this.refreshSession()

    const success = await this.refreshPromise
    this.isRefreshing = false
    this.refreshPromise = null

    if (!success) {
      this.redirectToLogin()
    }

    return success
  }

  // Attempt to refresh the session
  private static async refreshSession(): Promise<boolean> {
    try {
      // Use singleton supabase client instead of creating a new one
      const { data, error } = await supabase.auth.refreshSession()
      
      if (error || !data.session) {
        console.error('Failed to refresh session:', error)
        return false
      }

      // Clear any cached sessions to force re-read
      if (typeof window !== 'undefined') {
        sessionStorage.removeItem('supabase-session-cache')
      }

      return true
    } catch (error) {
      console.error('Error refreshing session:', error)
      return false
    }
  }

  // Notify user about expired login and redirect
  static notifyAndRedirectToLogin() {
    if (typeof window !== 'undefined') {
      // Show user-friendly notification using CSS classes from globals.css
      const notification = document.createElement('div')
      notification.className = 'toast-notification toast-notification--error'

      // SECURITY: Use safe DOM methods instead of innerHTML to prevent XSS
      const container = document.createElement('div')
      container.className = 'toast-notification__content'

      const icon = document.createElement('span')
      icon.textContent = '🔐'

      const message = document.createElement('span')
      message.className = 'toast-notification__message'
      message.textContent = 'Your login has expired. Redirecting to login page...'

      container.appendChild(icon)
      container.appendChild(message)
      notification.appendChild(container)
      document.body.appendChild(notification)

      // Auto-remove notification after redirect
      setTimeout(() => {
        if (notification.parentNode) {
          notification.parentNode.removeChild(notification)
        }
      }, 3000)

      // Clear all auth-related storage
      sessionStorage.removeItem('supabase-session-cache')
      localStorage.removeItem('supabase.auth.token')

      // Store current path to redirect back after login
      const currentPath = window.location.pathname + window.location.search
      if (currentPath !== '/login') {
        sessionStorage.setItem('redirectAfterLogin', currentPath)
      }

      // Redirect after brief delay to show notification
      setTimeout(() => {
        window.location.href = '/login'
      }, 1500)
    }
  }
  
  // Notify user about network issues
  static notifyNetworkError() {
    if (typeof window !== 'undefined') {
      // Check if notification already exists
      const existingNotification = document.getElementById('network-error-notification')
      if (existingNotification) return

      // Show user-friendly network error notification using CSS classes from globals.css
      const notification = document.createElement('div')
      notification.id = 'network-error-notification'
      notification.className = 'toast-notification toast-notification--warning'

      // SECURITY: Use safe DOM methods instead of innerHTML to prevent XSS
      const container = document.createElement('div')
      container.className = 'toast-notification__content'

      const icon = document.createElement('span')
      icon.textContent = '📡'

      const messageContainer = document.createElement('div')
      messageContainer.className = 'toast-notification__message'

      const title = document.createElement('div')
      title.textContent = 'Network connection issue'

      const subtitle = document.createElement('div')
      subtitle.className = 'toast-notification__subtitle'
      subtitle.textContent = 'Please check your connection and try again. Click to dismiss.'

      messageContainer.appendChild(title)
      messageContainer.appendChild(subtitle)
      container.appendChild(icon)
      container.appendChild(messageContainer)
      notification.appendChild(container)

      // Auto-remove on click or after 10 seconds
      const removeNotification = () => {
        if (notification.parentNode) {
          notification.parentNode.removeChild(notification)
        }
      }

      notification.addEventListener('click', removeNotification)
      setTimeout(removeNotification, 10000)

      document.body.appendChild(notification)
    }
  }
  
  // Legacy method for backward compatibility
  static redirectToLogin() {
    this.notifyAndRedirectToLogin()
  }

  // Reset retry count on successful request
  static resetRetryCount() {
    this.retryCount = 0
  }

  // Public getter for retry count
  static getRetryCount(): number {
    return this.retryCount
  }

  // Public getter for max retries
  static getMaxRetries(): number {
    return this.maxRetries
  }
}

// Simple fetch wrapper that detects auth failures and redirects to login
export async function fetchWithAuth(
  url: string,
  options: RequestInit & { skipAuthRetry?: boolean } = {}
): Promise<Response> {
  // Auto-add auth headers if user is logged in using our custom session storage
  let accessToken: string | null = null
  
  try {
    const sessionData = localStorage.getItem('supreme_session')
    if (sessionData) {
      const session = JSON.parse(sessionData)
      // Check if session is not expired
      if (session.expires_at && session.expires_at > Date.now() / 1000) {
        accessToken = session.access_token
      }
    }
  } catch (error) {
    console.error('Error reading session from localStorage:', error)
  }
  
  const headers = new Headers(options.headers)
  if (accessToken && !headers.get('Authorization')) {
    headers.set('Authorization', `Bearer ${accessToken}`)
  }
  
  const response = await fetch(url, {
    ...options,
    headers
  })

  // If successful, reset retry count
  if (response.ok) {
    AuthInterceptor.resetRetryCount()
    return response
  }

  // Skip auth retry if explicitly disabled
  if (options.skipAuthRetry) {
    return response
  }

  // Handle auth errors with user notifications - only redirect after multiple failures
  if (response.status === 401 || response.status === 403) {
    console.warn('Authentication failed - status:', response.status, 'Retry count:', AuthInterceptor.getRetryCount())

    // Only redirect after exhausting all retries, not on first failure
    if (AuthInterceptor.getRetryCount() >= AuthInterceptor.getMaxRetries() - 1) {
      if (typeof window !== 'undefined') {
        const currentPath = window.location.pathname
        // Only redirect for certain critical endpoints, not search
        if (currentPath.includes('/dashboard') || currentPath.includes('/target') || currentPath.includes('/implementation')) {
          console.error('Persistent auth failure after retries, redirecting to login')
          AuthInterceptor.notifyAndRedirectToLogin()
        } else {
          console.log('Non-critical auth failure, continuing without redirect')
        }
      }
    } else {
      console.log('Auth failure, will retry before redirecting')
    }
    return response
  }
  
  // Handle network errors
  if (!response.ok && (response.status === 0 || response.status >= 500)) {
    AuthInterceptor.notifyNetworkError()
    return response
  }

  return response
}